import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Plus } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

const faqs = [
  {
    question: 'What services does FuturePath Technologies offer?',
    answer: 'We specialize in e-commerce engineering — HCL Commerce implementations, headless migrations, and MedusaJS/Next.js storefronts — for B2B and B2C brands. We also take on custom software and integration projects that connect commerce platforms to ERP, PIM, and fulfillment systems.',
  },
  {
    question: 'How long does it take to complete a project?',
    answer: 'Project timelines vary based on complexity and requirements. A standard website takes 2-4 weeks, e-commerce platforms take 4-8 weeks, and custom software projects take 8-16 weeks. We provide detailed timelines during the consultation phase and keep you updated throughout the development process.',
  },
  {
    question: 'What is your development process?',
    answer: 'We follow a proven 6-step process: Discovery & Research, Planning & Strategy, Development & Design, Testing & QA, Launch & Deployment, and ongoing Support & Maintenance. This methodology ensures quality, transparency, and timely delivery.',
  },
  {
    question: 'Do you provide ongoing support after project completion?',
    answer: 'Yes! We offer comprehensive post-launch support including bug fixes, security updates, performance optimization, and feature enhancements. All projects include initial support, and we offer extended maintenance packages for long-term partnerships.',
  },
  {
    question: 'What technologies do you work with?',
    answer: 'We work with React, Next.js, Node.js, MedusaJS, and HCL Commerce — the stack behind modern headless and enterprise commerce platforms.',
  },
  {
    question: 'How much does a website or app cost?',
    answer: 'Project costs depend on scope and platform complexity — a MedusaJS storefront build and an HCL Commerce migration have very different pricing profiles. Book a consultation and we will provide a detailed quote based on your specific requirements.',
  },
  {
    question: 'Do you work with startups?',
    answer: 'Absolutely! We love working with startups and offer flexible solutions that fit startup budgets and timelines. We provide strategic guidance, MVP development, and scalable architectures that grow with your business.',
  },
  {
    question: 'Can you help with SEO and digital marketing?',
    answer: 'Yes! Our digital marketing team specializes in SEO optimization, Google Ads, social media marketing, content strategy, and conversion optimization. We create data-driven campaigns that deliver measurable results.',
  },
  {
    question: 'Do you offer mobile app development?',
    answer: 'Yes, we develop native iOS and Android apps as well as cross-platform solutions using React Native and Flutter. Our apps are designed for performance, security, and excellent user experience.',
  },
  {
    question: 'How do I get started?',
    answer: 'Getting started is easy! Simply book a free consultation through our website or contact us directly. We\'ll discuss your requirements, provide recommendations, and create a detailed proposal with timeline and pricing.',
  },
  {
    question: 'Do you sign NDAs?',
    answer: 'Yes, we respect client confidentiality and are happy to sign Non-Disclosure Agreements (NDAs) before discussing your project details. Your intellectual property and business information are always protected.',
  },
  {
    question: 'What payment methods do you accept?',
    answer: 'Payment is typically structured in milestones — for example, a portion upfront and the remainder tied to project milestones. We will agree on specific terms during the proposal stage',
  },
];

export default function FAQPage() {
  return (
    <>
      <Helmet>
        <title>FAQ - FuturePath Technologies</title>
        <meta name="description" content="Find answers to frequently asked questions about our services, pricing, process, and more." />
      </Helmet>

      <section className="relative min-h-[40vh] flex items-center justify-center pt-32 pb-20">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-secondary/10 to-transparent opacity-50" />
        <div className="container-custom relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-6xl font-bold font-outfit mb-6 text-white" data-testid="faq-hero-title">
              Frequently Asked Questions
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
              Everything you need to know about our services
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section-padding" data-testid="faq-list-section">
        <div className="container-custom max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="glass-card rounded-2xl px-6 border-none"
                  data-testid={`faq-item-${index}`}
                >
                  <AccordionTrigger className="text-left text-white font-outfit font-semibold hover:text-primary hover:no-underline py-6">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-6">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card rounded-3xl p-12 text-center mt-16"
          >
            <h2 className="text-3xl font-bold font-outfit mb-4 text-white">
              Still have questions?
            </h2>
            <p className="text-muted-foreground mb-6">
              Can't find the answer you're looking for? Get in touch with our team.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="inline-flex items-center justify-center rounded-full bg-primary hover:bg-primary/90 px-8 py-3 font-semibold text-white neon-glow transition-colors"
                data-testid="contact-us-btn"
              >
                Contact Us
              </a>
              <a
                href="/consultation"
                className="inline-flex items-center justify-center rounded-full border border-white/20 bg-white/5 hover:bg-white/10 px-8 py-3 font-semibold text-white transition-colors"
                data-testid="schedule-consultation-btn"
              >
                Schedule Consultation
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
